/**
 * O changelog do projeto: um listão do que mudou ou entrou, mais recente primeiro.
 *
 * <p>Não é a trilha de atividade, que já existe e registra toda mutação em silêncio para
 * auditoria. Este é editorial — alguém do time escreve à mão o que foi entregue —, para
 * quem não acompanhou o dia a dia entender o que mudou sem vasculhar cards e comentários.
 *
 * <p><b>Duas abas, um sistema por entrada.</b> GG Pilot é esta ferramenta de
 * planejamento; Mistik Island é o jogo em si, que roda no próprio {@code /admin} — um
 * sistema à parte, sem contrato com este. Misturar as duas listas faria "o que mudou no
 * jogo" e "o que mudou na ferramenta que planeja o jogo" parecerem a mesma pergunta.
 *
 * <p>Por ora só é alcançada pelo botão no Dev Board, sem entrada no menu lateral.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TextoRico } from '../TextoRico';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import * as api from './changelogApi';
import type { EntradaDeChangelog, ListaDeChangelog, Sistema } from './changelogApi';
import { CHAVE_CHANGELOG, dataPorExtenso } from './changelogApi';
import './changelog.css';

const ABAS: { valor: Sistema; rotulo: string }[] = [
  { valor: 'GG_PILOT', rotulo: 'GG Pilot' },
  { valor: 'MISTIK_ISLAND', rotulo: 'Mistik Island' },
];

/** O dia de hoje em ISO, no fuso de quem está olhando — e não em UTC. */
function hojeISO(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

export function Changelog() {
  const fila = useQueryClient();
  const [aba, setAba] = useState<Sistema>('GG_PILOT');
  const [lancando, setLancando] = useState(false);
  const [data, setData] = useState(hojeISO());
  const [tema, setTema] = useState('');
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [rascunho, setRascunho] = useState({ data: '', tema: '', texto: '' });

  const { data: lista, isPending, error } = useQuery<ListaDeChangelog>({
    queryKey: CHAVE_CHANGELOG(aba),
    queryFn: () => api.listar(aba),
  });

  const { data: temasUsados } = useQuery<string[]>({
    queryKey: ['changelog-temas', aba],
    queryFn: () => api.temas(aba),
  });

  const recarregar = () => void fila.invalidateQueries({ queryKey: CHAVE_CHANGELOG(aba) });

  const lancar = useMutation({
    mutationFn: () => api.lancar({ sistema: aba, data, tema: tema.trim(), texto: texto.trim() }),
    onSuccess: () => {
      recarregar();
      void fila.invalidateQueries({ queryKey: ['changelog-temas', aba] });
      fecharFormulario();
    },
    onError: (e: Error) => setErro(e.message),
  });

  const salvar = useMutation({
    mutationFn: ({ id, data, tema, texto }: { id: number; data: string; tema: string; texto: string }) =>
      api.salvar(id, { data, tema, texto }),
    onSuccess: () => {
      recarregar();
      void fila.invalidateQueries({ queryKey: ['changelog-temas', aba] });
      setEditando(null);
    },
    onError: (e: Error) => setErro(e.message),
  });

  const excluirEntrada = useMutation({
    mutationFn: (id: number) => api.excluir(id),
    onSuccess: recarregar,
    onError: (e: Error) => setErro(e.message),
  });

  function fecharFormulario() {
    setLancando(false);
    setData(hojeISO());
    setTema('');
    setTexto('');
    setErro(null);
  }

  function abrirEdicao(e: EntradaDeChangelog) {
    setEditando(e.id);
    setRascunho({ data: e.data, tema: e.tema ?? '', texto: e.texto });
  }

  const entradas = lista?.entradas ?? [];

  return (
    <div className="cl">
      <TituloDaPagina rotulo="Changelog">
        O que foi entregue, mais recente primeiro.
      </TituloDaPagina>

      <div className="cl-abas" role="tablist">
        {ABAS.map((a) => (
          <button
            key={a.valor}
            role="tab"
            aria-selected={aba === a.valor}
            className={`cl-aba${aba === a.valor ? ' on' : ''}`}
            onClick={() => {
              setAba(a.valor);
              fecharFormulario();
              setEditando(null);
            }}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      {erro && (
        <div className="cl-erro" role="alert">
          {erro}
          <button onClick={() => setErro(null)} aria-label="Fechar">
            ×
          </button>
        </div>
      )}

      {isPending ? (
        <div className="rm-estado">carregando o changelog…</div>
      ) : error ? (
        <div className="rm-estado">não consegui carregar: {error.message}</div>
      ) : (
        <>
          {lancando ? (
            <form
              className="cl-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (texto.trim()) lancar.mutate();
              }}
            >
              <datalist id="cl-temas">
                {(temasUsados ?? []).map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>

              <div className="cl-campos">
                <label className="cl-campo cl-data">
                  <span>Data</span>
                  <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
                </label>

                <label className="cl-campo">
                  <span>Tema (opcional)</span>
                  <input
                    type="text"
                    list="cl-temas"
                    value={tema}
                    placeholder="Ex.: Cenas, Admin, Pipeline"
                    onChange={(e) => setTema(e.target.value)}
                  />
                </label>

                <label className="cl-campo cl-largo">
                  <span>O que mudou ou entrou, em {ABAS.find((a) => a.valor === aba)?.rotulo}</span>
                  <textarea
                    value={texto}
                    autoFocus
                    rows={3}
                    placeholder="Ex.: Tags coloridas nos cards do Dev Board."
                    onChange={(e) => setTexto(e.target.value)}
                  />
                </label>
              </div>

              <div className="cl-form-acoes">
                <button type="submit" className="cl-btn" disabled={!texto.trim() || lancar.isPending}>
                  {lancar.isPending ? 'Lançando…' : 'Lançar'}
                </button>
                <button type="button" className="cl-btn fantasma" onClick={fecharFormulario}>
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
            <button className="cl-nova" onClick={() => setLancando(true)}>
              + nova entrada
            </button>
          )}

          {entradas.length === 0 ? (
            <div className="cl-vazio">
              <b>Nenhuma entrada ainda</b>
              Lance a data e o que mudou ou entrou, e o listão começa aqui.
            </div>
          ) : (
            <ol className="cl-lista">
              {entradas.map((e) => (
                <li key={e.id} className="cl-linha">
                  {editando === e.id ? (
                    <form
                      className="cl-edicao"
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        if (rascunho.texto.trim()) {
                          salvar.mutate({ id: e.id, ...rascunho, texto: rascunho.texto.trim() });
                        }
                      }}
                    >
                      <label className="cl-campo">
                        <span>Data</span>
                        <input
                          type="date"
                          value={rascunho.data}
                          onChange={(ev) => setRascunho({ ...rascunho, data: ev.target.value })}
                        />
                      </label>

                      <label className="cl-campo">
                        <span>Tema (opcional)</span>
                        <input
                          type="text"
                          list="cl-temas"
                          value={rascunho.tema}
                          placeholder="Ex.: Cenas, Admin, Pipeline"
                          onChange={(ev) => setRascunho({ ...rascunho, tema: ev.target.value })}
                        />
                      </label>

                      <label className="cl-campo cl-largo">
                        <span>O que mudou ou entrou</span>
                        <textarea
                          value={rascunho.texto}
                          rows={3}
                          autoFocus
                          onChange={(ev) => setRascunho({ ...rascunho, texto: ev.target.value })}
                        />
                      </label>

                      <div className="cl-edicao-acoes">
                        <button type="submit" className="cl-btn" disabled={salvar.isPending}>
                          {salvar.isPending ? 'Salvando…' : 'Salvar'}
                        </button>
                        <button type="button" className="cl-btn fantasma" onClick={() => setEditando(null)}>
                          Cancelar
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="cl-item">
                      <span className="cl-dia">{dataPorExtenso(e.data)}</span>
                      {e.tema && <span className="cl-tema">{e.tema}</span>}
                      <span className="cl-texto">
                        <TextoRico texto={e.texto} membros={[]} />
                      </span>
                      <span className="cl-acoes">
                        <button
                          type="button"
                          className="cl-lapis"
                          title="Editar"
                          aria-label={`Editar entrada de ${dataPorExtenso(e.data)}`}
                          onClick={() => abrirEdicao(e)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="cl-tirar"
                          title="Excluir"
                          aria-label={`Excluir entrada de ${dataPorExtenso(e.data)}`}
                          onClick={() => {
                            if (confirm('Excluir esta entrada do changelog?')) excluirEntrada.mutate(e.id);
                          }}
                        >
                          ×
                        </button>
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}
